import { SearchOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Button, Card, CardContent, Divider, Grid, TextField, Typography } from '@mui/material'
import { ExplanationAccordion, LabelField, UnsavedChangesPrompt } from 'components/'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { MoveVehicleDto, moveVehicleValidationSchema, VehicleDto } from 'dtos'
import { Formik } from 'formik'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export default function MoveVehicle() {
  const layout = useLayout()
  const navigate = useNavigate()

  const [search, setSearch] = useState<string>('')
  const [searchError, setSearchError] = useState<string>('')

  useEffect(() => {
    layout.setTitle('Move Vehicle')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  return (
    <Formik
      enableReinitialize={true}
      initialValues={new MoveVehicleDto()}
      onSubmit={(values, formikHelpers) => {
        layout.setIsLayoutLoading(true)
        controllers
          .moveVehicle(values)
          .then(response => {
            enqueueSnackbar('Vehicle Moved Successfully!', { variant: 'success' })
            setSearch('')
            setSearchError('')
            formikHelpers.resetForm({ values: new MoveVehicleDto() })
          })
          .finally(() => {
            layout.setIsLayoutLoading(false)
            formikHelpers.setSubmitting(false)
          })
      }}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={moveVehicleValidationSchema}
    >
      {({ dirty, errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, setValues, touched, values }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Card>
              <CardContent>
                <Grid container spacing={1} alignItems='center'>
                  <Grid item xs={12}>
                    <Typography fontWeight='bold' variant='h6'>
                      Move Vehicle
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      autoFocus
                      error={Boolean(searchError)}
                      helperText={searchError}
                      fullWidth
                      label='Last 6 of VIN or Importer Stock #'
                      onChange={e => {
                        setSearch(e.target.value)
                      }}
                      value={search}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      endIcon={<SearchOutlined />}
                      fullWidth
                      onClick={() => {
                        layout.setIsLayoutLoading(true)
                        controllers
                          .getVehicleToMove(search)
                          .then(response => {
                            if (response.value) {
                              setValues(response.value)

                              // Need this timeout to focus the Parking Space text field, because it is initially disabled
                              // on state.
                              // Maxwell Sommer 10/06/2024
                              setTimeout(() => {
                                const parkingSpaceInput = document.querySelector(
                                  'input[name="parkingSpace"]'
                                ) as HTMLInputElement

                                if (parkingSpaceInput) {
                                  parkingSpaceInput.focus()
                                }
                              }, 100)

                              setSearch('')
                              setSearchError('')
                            } else {
                              setValues(new VehicleDto())
                              setSearchError('No results found.')
                            }
                          })
                          .finally(() => {
                            layout.setIsLayoutLoading(false)
                          })
                      }}
                      variant='text'
                    >
                      SEARCH
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='VIN' value={values.vin} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Year' value={values.year} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Make' value={values.make} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Model' value={values.model} />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={!values.vehicleGuid}
                      error={Boolean(touched.parkingSpace && errors.parkingSpace)}
                      fullWidth
                      helperText={touched.parkingSpace && errors.parkingSpace}
                      label='Parking Space'
                      name='parkingSpace'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.parkingSpace || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || The expected use-case of the Move Vehicles feature is that Registered Importer team members will move a
                      vehicle to a new location, get out of the vehicle and use their phone’s barcode scanner to quickly input
                      the “LAST 6 OF VIN” or “Importer Stock #” into the input field, press Search, enter the new “Parking
                      Space”, and press “SAVE”. || Users may enter the “Last 6 of VIN” or “Importer Stock #” into the search
                      field, press the SEARCH button, and see the matching vehicle record. The matching vehicle record’s VIN,
                      Year, Make, and Model will be displayed, and the “Parking Space” input field will be populated with
                      whatever the current “Parking Space” is on the vehicle. A user may modify the “Parking Space” and then
                      press “SAVE” to update the “Parking Space” information of the vehicle. The “Parking Space” is a required
                      field but has no unique value checks. || If no match is found, red text will appear near the search field
                      saying, “No results found.” || If there is more than one vehicle record that is active in VTS2 with
                      identical “Last 6 of VIN”, the site will display the active matching vehicle with the oldest “Created
                      date” in the VTS2 database. ||
                    </ExplanationAccordion>
                  </Grid>

                  <Grid item xs={12} container spacing={1} alignItems='center' justifyContent='flex-end'>
                    <Grid item>
                      <Button
                        color='inherit'
                        onClick={() => {
                          navigate(-1)
                        }}
                        variant='text'
                      >
                        CANCEL
                      </Button>
                    </Grid>

                    <Grid item>
                      <LoadingButton disabled={!values.vehicleGuid} loading={false} type='submit' variant='contained'>
                        SAVE
                      </LoadingButton>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <UnsavedChangesPrompt when={dirty} />
          </form>
        )
      }}
    </Formik>
  )
}
